import { z } from "zod";
import { IdSchema } from "./primitives.js";
/**
 * Clinic order sets: a named workup ("STI screening") with its coded tests, the forms it uses and
 * exactly where each test prints on each form. An administrator fills them in once; in a visit the
 * agent may apply a published set, and the server expands it into its tests. A set only acts on an
 * order that carries its id, so clinics without sets (and orders without one) fill forms exactly as
 * before.
 */
const KeySchema = z.string().trim().min(1).max(160);
/** Set ids are also the ids of the clinic instructions that hold them. */
export const FormOrderSetIdSchema = z
    .string()
    .regex(/^order-set\.[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u);
/** The set a lab order was drafted from. The server sets it; its name is the published name. */
export const LabOrderSetRefSchema = z.strictObject({
    id: FormOrderSetIdSchema,
    name: z.string().trim().min(1).max(120),
});
/** The pseudo-concept that says "this order was drafted from set X". Never a test. */
export const FORM_ORDER_SET_CONCEPT_PREFIX = "ORDER_SET|";
export function formOrderSetConceptId(setId) {
    return `${FORM_ORDER_SET_CONCEPT_PREFIX}${setId}`;
}
export function isFormOrderSetConceptId(id) {
    return id.startsWith(FORM_ORDER_SET_CONCEPT_PREFIX);
}
export const FormOrderSetTestSchema = z.strictObject({
    /** `LOINC|<code>` of a coded lab-test catalogue entry. */
    conceptId: KeySchema,
    specimen: z.string().trim().min(1).max(160),
    /** Coded tests with unknown fasting are refused when the order is accepted. */
    fasting: z.enum(["required", "not_required"]),
});
/** Lines filled top to bottom ("Other Tests" lines 1-11). Each test takes the next free line. */
export const FormOrderSetListSchema = z.strictObject({
    id: z.string().regex(/^list\.[a-z0-9-]{1,40}$/u),
    documentId: IdSchema,
    fieldIds: z.array(KeySchema).min(1).max(30),
});
const PlacementTextSchema = z.string().trim().min(1).max(240);
/**
 * Where the set prints. A placement tied to a test (`testConceptId`) prints only while that test
 * is still on the order; an untied one prints whenever the set is applied. A tick, a line or tied
 * text places its test on that form, and the set's other forms then leave that test off their own
 * boxes and ordered-test lists, unless the administrator chose “Also print it here” (`auto`).
 */
export const FormOrderSetPlacementSchema = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("tick"),
        documentId: IdSchema,
        fieldId: KeySchema,
        testConceptId: KeySchema.nullable(),
    }),
    z.strictObject({
        kind: z.literal("text"),
        documentId: IdSchema,
        fieldId: KeySchema,
        testConceptId: KeySchema.nullable(),
        text: PlacementTextSchema,
    }),
    z.strictObject({
        kind: z.literal("line"),
        listId: z.string().regex(/^list\.[a-z0-9-]{1,40}$/u),
        testConceptId: KeySchema,
        text: PlacementTextSchema,
    }),
    /**
     * “Also print it here”: the set places this test on another of its forms, and this form still
     * prints it its own way (its box for the test, or its ordered-test lines). Without it, a set
     * form where the set does not place a test it places elsewhere leaves that test off its own
     * boxes and lists. It fills nothing itself.
     */
    z.strictObject({
        kind: z.literal("auto"),
        documentId: IdSchema,
        testConceptId: KeySchema,
    }),
]);
/**
 * `placements` counts the places a set fills (ticks, text and lines); `alsoPrint` counts its
 * “Also print it here” choices, at most one per test on each of its other forms.
 */
export const FORM_ORDER_SET_LIMITS = {
    tests: 10,
    forms: 3,
    lists: 6,
    placements: 20,
    alsoPrint: 20,
};
/**
 * Cross-field rules (placements point at the set's own tests, forms and lists) are checked by the
 * domain when a set is saved, not here: instruction schemas are extended, and refined objects
 * cannot be.
 */
export const FormOrderSetSchema = z.strictObject({
    /** One sentence, so the set is exactly one clause of its instruction. */
    name: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[^.!?;:\r\n]+$/u, "Leave out . ! ? ; and : in the order set's name."),
    whenToUse: z.string().trim().min(1).max(500),
    tests: z.array(FormOrderSetTestSchema).min(1).max(FORM_ORDER_SET_LIMITS.tests),
    forms: z.array(IdSchema).min(1).max(FORM_ORDER_SET_LIMITS.forms),
    lists: z.array(FormOrderSetListSchema).max(FORM_ORDER_SET_LIMITS.lists),
    /** The limit on filled places alone is checked by the domain when the set is saved. */
    placements: z
        .array(FormOrderSetPlacementSchema)
        .min(1)
        .max(FORM_ORDER_SET_LIMITS.placements + FORM_ORDER_SET_LIMITS.alsoPrint),
});
/** The generated text of a set's instruction: always one clause. */
export function formOrderSetText(set) {
    return `Order set “${set.name.trim()}”`;
}
/** A published set as the agent sees it when drafting a plan. */
export const ClinicOrderSetOptionSchema = z.strictObject({
    id: FormOrderSetIdSchema,
    name: z.string().trim().min(1).max(120),
    whenToUse: z.string().trim().min(1).max(500),
    tests: z
        .array(z.strictObject({
        code: z.string().min(1).max(80),
        codeSystem: z.literal("LOINC"),
        display: z.string().min(1).max(240),
        specimen: z.string().min(1).max(160),
        fasting: z.enum(["required", "not_required"]),
    }))
        .min(1)
        .max(FORM_ORDER_SET_LIMITS.tests),
});
//# sourceMappingURL=form-order-sets.js.map